/**
 * Packers / stock Web Audio feedback tones (no asset files).
 *
 * Scan kinds: success | attention | error
 * Stock / Packers: stockInject | stocktake | pack | unpack | ship | unship | shortStock
 */

let sharedCtx = null;
const recentPlayByKey = new Map();
const DEDUPE_MS = 1800;

function getAudioContext() {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!sharedCtx) sharedCtx = new AC();
  return sharedCtx;
}

async function ensureRunning(ctx) {
  if (!ctx) return null;
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      /* autoplay policies */
    }
  }
  return ctx;
}

function tone(ctx, { frequency, duration = 0.08, type = 'sine', gain = 0.06, when = 0 }) {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.value = frequency;
  g.gain.value = gain;
  osc.connect(g);
  g.connect(ctx.destination);
  const start = ctx.currentTime + when;
  osc.start(start);
  g.gain.setValueAtTime(gain, start);
  g.gain.exponentialRampToValueAtTime(0.001, start + duration);
  osc.stop(start + duration + 0.02);
}

/**
 * Skip duplicate plays of the same logical event (toast + Packers listener).
 * @param {string} [dedupeKey]
 * @returns {boolean}
 */
export function shouldPlayWebAudioFeedback(dedupeKey) {
  if (!dedupeKey) return true;
  const now = Date.now();
  const last = recentPlayByKey.get(dedupeKey);
  if (last != null && now - last < DEDUPE_MS) return false;
  recentPlayByKey.set(dedupeKey, now);
  if (recentPlayByKey.size > 200) {
    for (const [k, t] of recentPlayByKey) {
      if (now - t > DEDUPE_MS * 4) recentPlayByKey.delete(k);
    }
  }
  return true;
}

function maybeVibrate(pattern) {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(pattern);
    }
  } catch {
    /* ignore */
  }
}

/**
 * @param {'success'|'attention'|'error'|'stockInject'|'stocktake'|'pack'|'unpack'|'ship'|'unship'|'shortStock'|'item_ok'|'item_wrong'|'item_complete'} kind
 * @param {{ dedupeKey?: string }} [opts]
 */
export async function playWebAudioFeedback(kind = 'success', opts = {}) {
  try {
    if (!shouldPlayWebAudioFeedback(opts.dedupeKey)) return;

    const ctx = await ensureRunning(getAudioContext());
    if (!ctx) return;

    if (kind === 'item_ok') {
      tone(ctx, { frequency: 700, duration: 0.06, gain: 0.055, when: 0 });
      tone(ctx, { frequency: 920, duration: 0.08, gain: 0.05, when: 0.06 });
      maybeVibrate(12);
      return;
    }

    if (kind === 'item_wrong') {
      tone(ctx, { frequency: 200, duration: 0.14, type: 'square', gain: 0.05, when: 0 });
      tone(ctx, { frequency: 160, duration: 0.18, type: 'square', gain: 0.045, when: 0.15 });
      maybeVibrate([40, 30, 40]);
      return;
    }

    if (kind === 'item_complete') {
      tone(ctx, { frequency: 587, duration: 0.07, type: 'triangle', gain: 0.055, when: 0 });
      tone(ctx, { frequency: 740, duration: 0.08, type: 'triangle', gain: 0.05, when: 0.08 });
      tone(ctx, { frequency: 988, duration: 0.14, type: 'triangle', gain: 0.045, when: 0.17 });
      maybeVibrate([18, 20, 18]);
      return;
    }

    if (kind === 'success') {
      // Soft two-note "added to cart"
      tone(ctx, { frequency: 660, duration: 0.07, gain: 0.055, when: 0 });
      tone(ctx, { frequency: 880, duration: 0.1, gain: 0.05, when: 0.07 });
      return;
    }

    if (kind === 'attention') {
      // Distinct mid chirp — temp sample or noteworthy scan
      tone(ctx, { frequency: 520, duration: 0.09, type: 'triangle', gain: 0.07, when: 0 });
      tone(ctx, { frequency: 780, duration: 0.09, type: 'triangle', gain: 0.065, when: 0.11 });
      tone(ctx, { frequency: 520, duration: 0.12, type: 'triangle', gain: 0.06, when: 0.22 });
      return;
    }

    if (kind === 'error') {
      tone(ctx, { frequency: 220, duration: 0.12, type: 'square', gain: 0.045, when: 0 });
      tone(ctx, { frequency: 180, duration: 0.16, type: 'square', gain: 0.04, when: 0.14 });
      return;
    }

    if (kind === 'stockInject') {
      // Bright rising chime — stock arrived / injection toast
      tone(ctx, { frequency: 523, duration: 0.08, type: 'triangle', gain: 0.06, when: 0 });
      tone(ctx, { frequency: 659, duration: 0.09, type: 'triangle', gain: 0.055, when: 0.09 });
      tone(ctx, { frequency: 784, duration: 0.14, type: 'triangle', gain: 0.05, when: 0.18 });
      return;
    }

    if (kind === 'stocktake') {
      tone(ctx, { frequency: 440, duration: 0.1, type: 'sine', gain: 0.05, when: 0 });
      tone(ctx, { frequency: 554, duration: 0.14, type: 'sine', gain: 0.045, when: 0.12 });
      return;
    }

    if (kind === 'pack') {
      tone(ctx, { frequency: 587, duration: 0.08, type: 'sine', gain: 0.055, when: 0 });
      tone(ctx, { frequency: 740, duration: 0.12, type: 'sine', gain: 0.05, when: 0.09 });
      return;
    }

    if (kind === 'unpack') {
      tone(ctx, { frequency: 740, duration: 0.08, type: 'sine', gain: 0.045, when: 0 });
      tone(ctx, { frequency: 587, duration: 0.12, type: 'sine', gain: 0.04, when: 0.1 });
      return;
    }

    if (kind === 'ship') {
      tone(ctx, { frequency: 494, duration: 0.07, type: 'triangle', gain: 0.055, when: 0 });
      tone(ctx, { frequency: 659, duration: 0.08, type: 'triangle', gain: 0.05, when: 0.08 });
      tone(ctx, { frequency: 880, duration: 0.14, type: 'triangle', gain: 0.045, when: 0.17 });
      return;
    }

    if (kind === 'unship') {
      tone(ctx, { frequency: 880, duration: 0.07, type: 'triangle', gain: 0.045, when: 0 });
      tone(ctx, { frequency: 659, duration: 0.08, type: 'triangle', gain: 0.04, when: 0.09 });
      tone(ctx, { frequency: 494, duration: 0.12, type: 'triangle', gain: 0.035, when: 0.18 });
      return;
    }

    if (kind === 'shortStock') {
      tone(ctx, { frequency: 400, duration: 0.1, type: 'square', gain: 0.04, when: 0 });
      tone(ctx, { frequency: 320, duration: 0.14, type: 'square', gain: 0.035, when: 0.12 });
      return;
    }

    // Fallback: success
    tone(ctx, { frequency: 660, duration: 0.07, gain: 0.055, when: 0 });
    tone(ctx, { frequency: 880, duration: 0.1, gain: 0.05, when: 0.07 });
  } catch {
    /* ignore */
  }
}

/**
 * @param {'success'|'attention'|'error'|'item_ok'|'item_wrong'|'item_complete'} kind
 * @param {{ dedupeKey?: string }} [opts]
 */
export async function playOrderQrScanFeedback(kind = 'success', opts = {}) {
  return playWebAudioFeedback(kind, opts);
}

/**
 * Map stock-update notification types to feedback kinds.
 * @param {string} type
 * @returns {'stockInject'|'stocktake'|null}
 */
export function resolveStockUpdateFeedbackKind(type) {
  if (type === 'stock_injection') return 'stockInject';
  if (type === 'stocktake_completed') return 'stocktake';
  return null;
}

/**
 * Default feedback kind when Packers does not supply a resolver.
 * @param {{ outcome: 'success'|'outside'|'missing'|'invalid', tempSampled?: boolean }} opts
 */
export function resolveDefaultOrderQrScanFeedbackKind({
  outcome = 'success',
  tempSampled = false,
} = {}) {
  if (outcome === 'invalid' || outcome === 'missing') return 'error';
  if (outcome === 'outside') return 'attention';
  if (tempSampled) return 'attention';
  return 'success';
}
